// The playground's HTTP client (PG-2a, planner/lender-hub/http-client-plan.md): Postman inside the Hub. A request as
// the screen holds it (method, URL, params, authorization, headers, body, settings), the request hub-http sends from
// the lender's sandbox, "paste a curl", and the response's cookies. Pure functions: the screen and the tests use them.
// The history lives in the page while it is open; PG-2b: collections in the Hub (http-request-item), {{VARIABLES}} of
// the lender (filled in inside the sandbox, never here), form-data and binary bodies, and a Postman collection import.
import type { z } from 'zod'
import type { HttpRequestItem, HttpRequestSpec } from '@/contract/schemas'
import type { HttpAnswer, HttpFormPart, HttpRequest } from '@/lib/agent'

export const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const
export type Method = (typeof METHODS)[number]
export const METHOD_COLOUR: Record<Method, string> = {
  GET: 'text-emerald-600 dark:text-emerald-400', POST: 'text-amber-600 dark:text-amber-400', PUT: 'text-sky-600 dark:text-sky-400',
  PATCH: 'text-violet-600 dark:text-violet-300', DELETE: 'text-red-600 dark:text-red-400', HEAD: 'text-slate-500', OPTIONS: 'text-pink-600 dark:text-pink-400',
}

export interface Kv { key: string; value: string; on: boolean }
export type AuthType = 'none' | 'bearer' | 'basic' | 'apikey'
export type BodyMode = 'none' | 'raw' | 'urlencoded' | 'formdata' | 'binary'
export type RawType = 'json' | 'text' | 'xml'
/** a file chosen in the browser (kept in the page only: a saved request keeps its name) */
export interface PickedFile { name: string; type: string; size: number; base64: string }
export interface FormRow extends Kv { type: 'text' | 'file'; filename?: string; file?: PickedFile }
export interface HttpDraft {
  id: string
  name: string
  /** the collection folder ('' = the top); savedId = the saved request it is ('' = not saved) */
  folder: string
  savedId: string | null
  method: Method
  url: string
  params: Kv[]
  auth: { type: AuthType; token: string; username: string; password: string; key: string; value: string; in: 'header' | 'query' }
  headers: Kv[]
  body: { mode: BodyMode; raw: string; rawType: RawType; urlencoded: Kv[]; formdata: FormRow[]; binary: { filename: string; file?: PickedFile } | null }
  settings: { timeoutMs: number; followRedirects: boolean }
}
export type SavedHttpRequest = z.infer<typeof HttpRequestItem>
export type HttpSpec = z.infer<typeof HttpRequestSpec>

const newId = () => `h_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
export const emptyDraft = (patch: Partial<HttpDraft> = {}): HttpDraft => ({
  id: newId(), name: '', folder: '', savedId: null, method: 'GET', url: '', params: [],
  auth: { type: 'none', token: '', username: '', password: '', key: 'x-api-key', value: '', in: 'header' },
  headers: [], body: { mode: 'none', raw: '', rawType: 'json', urlencoded: [], formdata: [], binary: null },
  settings: { timeoutMs: 30000, followRedirects: true },
  ...patch,
})

/** What a collection keeps of a draft: no file contents (only their names). */
export function toSpec(d: HttpDraft): HttpSpec {
  return {
    method: d.method, url: d.url, params: d.params, auth: d.auth, headers: d.headers,
    body: {
      mode: d.body.mode, raw: d.body.raw, rawType: d.body.rawType, urlencoded: d.body.urlencoded,
      formdata: d.body.formdata.map(({ key, value, on, type, filename, file }) => ({ key, value: type === 'file' ? '' : value, on, type, ...(type === 'file' ? { filename: file?.name ?? filename ?? '' } : {}) })),
      binary: d.body.binary ? { filename: d.body.binary.file?.name ?? d.body.binary.filename } : null,
    },
    settings: d.settings,
  }
}
/** A saved request opened again (files to be chosen again). */
export const draftOfSaved = (it: SavedHttpRequest): HttpDraft => emptyDraft({
  name: it.name, folder: it.folder, savedId: it.id, method: it.request.method, url: it.request.url, params: it.request.params, auth: it.request.auth, headers: it.request.headers,
  body: { ...it.request.body, formdata: it.request.body.formdata.map((r) => ({ ...r })), binary: it.request.body.binary ? { filename: it.request.body.binary.filename } : null },
  settings: it.request.settings,
})
/** How many key values the Hub emptied on Save (a key is kept only as a {{VARIABLE}}). */
export function keysRemoved(before: HttpSpec, after: HttpSpec): number {
  const vals = (s: HttpSpec) => [s.auth.token, s.auth.password, s.auth.value, s.url, ...s.headers.map((h) => h.value), ...s.params.map((p) => p.value), ...s.body.urlencoded.map((p) => p.value), ...s.body.formdata.map((p) => p.value)]
  const a = vals(before)
  const b = vals(after)
  return a.filter((v, i) => v !== b[i]).length
}

// ---- {{VARIABLES}} --------------------------------------------------------------------------------------------------

const VAR = /\{\{\s*(\$?[A-Za-z_][A-Za-z0-9_.-]*)\s*\}\}/g
export const DYNAMIC_VARS = ['$guid', '$timestamp', '$isoTimestamp', '$randomInt']
/** Every {{NAME}} the draft uses (the address, auth, headers, params, bodies), without the dynamic ones. */
export function varsIn(d: HttpDraft): string[] {
  const texts = [d.url, d.auth.token, d.auth.username, d.auth.password, d.auth.key, d.auth.value, ...d.headers.flatMap((h) => (h.on ? [h.key, h.value] : [])), ...(d.body.mode === 'raw' ? [d.body.raw] : []), ...(d.body.mode === 'urlencoded' ? d.body.urlencoded.flatMap((r) => (r.on ? [r.key, r.value] : [])) : []), ...(d.body.mode === 'formdata' ? d.body.formdata.flatMap((r) => (r.on && r.type === 'text' ? [r.key, r.value] : [])) : [])]
  const out = new Set<string>()
  for (const t of texts) for (const m of String(t ?? '').matchAll(VAR)) if (!DYNAMIC_VARS.includes(m[1]!)) out.add(m[1]!)
  return [...out]
}
/** "What is our IP?": the address the Hub's requests leave from, as the internet sees it */
export const ipCheckDraft = () => emptyDraft({ name: 'What is our IP?', url: 'https://api.ipify.org?format=json', params: [{ key: 'format', value: 'json', on: true }] })

// ---- URL ⇄ Params (as Postman: typing in one changes the other) -------------------------------------------------

const splitUrl = (url: string) => {
  const hash = url.indexOf('#')
  const noHash = hash >= 0 ? url.slice(0, hash) : url
  const q = noHash.indexOf('?')
  return { base: q >= 0 ? noHash.slice(0, q) : noHash, query: q >= 0 ? noHash.slice(q + 1) : null }
}
const decodeSafe = (s: string) => { try { return decodeURIComponent(s.replace(/\+/g, ' ')) } catch { return s } }

/** The URL's query as Params rows; the switched-off rows already there stay (they are not in the URL). */
export function paramsFromUrl(url: string, current: Kv[] = []): Kv[] {
  const { query } = splitUrl(url)
  const on = query === null || query === '' ? [] : query.split('&').map((part) => {
    const i = part.indexOf('=')
    return { key: decodeSafe(i >= 0 ? part.slice(0, i) : part), value: i >= 0 ? decodeSafe(part.slice(i + 1)) : '', on: true }
  })
  return [...on, ...current.filter((p) => !p.on)]
}

/** The URL with the switched-on Params as its query (written as typed; encoded when sent). */
export function urlWithParams(url: string, params: Kv[]): string {
  const { base } = splitUrl(url)
  const q = params.filter((p) => p.on && p.key).map((p) => (p.value === '' ? p.key : `${p.key}=${p.value}`)).join('&')
  return q ? `${base}?${q}` : base
}

// ---- the request hub-http sends -----------------------------------------------------------------------------------

const has = (pairs: [string, string][], name: string) => pairs.some(([k]) => k.toLowerCase() === name.toLowerCase())
const base64 = (s: string) => btoa(Array.from(new TextEncoder().encode(s), (b) => String.fromCharCode(b)).join(''))
const RAW_TYPE: Record<RawType, string> = { json: 'application/json', text: 'text/plain', xml: 'application/xml' }

/** The headers the Hub adds by itself (shown greyed under Headers, as Postman does); a row you type wins. */
export function autoHeaders(d: HttpDraft): [string, string][] {
  const own = d.headers.filter((h) => h.on && h.key).map((h) => [h.key, h.value] as [string, string])
  const out: [string, string][] = []
  if (d.auth.type === 'bearer' && d.auth.token) out.push(['Authorization', `Bearer ${d.auth.token}`])
  if (d.auth.type === 'basic' && (d.auth.username || d.auth.password)) out.push(['Authorization', `Basic ${base64(`${d.auth.username}:${d.auth.password}`)}`])
  if (d.auth.type === 'apikey' && d.auth.in === 'header' && d.auth.key) out.push([d.auth.key, d.auth.value])
  if (d.body.mode === 'raw' && d.body.raw) out.push(['Content-Type', RAW_TYPE[d.body.rawType]])
  if (d.body.mode === 'urlencoded') out.push(['Content-Type', 'application/x-www-form-urlencoded'])
  if (d.body.mode === 'formdata') out.push(['Content-Type', 'multipart/form-data; boundary=<made when sent>'])
  if (d.body.mode === 'binary' && d.body.binary?.file) out.push(['Content-Type', d.body.binary.file.type || 'application/octet-stream'])
  out.push(['User-Agent', 'FirstfundLenderHub/1.0'], ['Accept', '*/*'])
  return out.filter(([k]) => !has(own, k))
}

/** The draft as hub-http takes it; or what is wrong with it, in plain words. */
export function buildRequest(d: HttpDraft): { ok: true; request: HttpRequest } | { ok: false; error: string } {
  let url = d.url.trim()
  if (!url) return { ok: false, error: 'Type the address first, e.g. https://uat.lender.example/v1/leads or {{LENDER_BASE_URL}}/v1/leads' }
  const withVars = url.includes('{{')
  if (!/^https?:\/\//i.test(url) && !/^\{\{/.test(url)) return { ok: false, error: 'The address must start with https://, http:// or a {{VARIABLE}}' }
  if (d.auth.type === 'apikey' && d.auth.in === 'query' && d.auth.key) url = urlWithParams(url, [...paramsFromUrl(url), { key: d.auth.key, value: d.auth.value, on: true }])
  // with {{VARIABLES}} the address is whole only in the sandbox (filled in there): sent as typed, checked there
  if (!withVars) {
    try {
      url = new URL(url).toString()
    } catch {
      return { ok: false, error: `“${url}” is not a full address` }
    }
  }
  const own = d.headers.filter((h) => h.on && h.key.trim()).map((h) => [h.key.trim(), h.value] as [string, string])
  // multipart's Content-Type (with its boundary) is made when sent; a binary file's type is its own
  const headers: [string, string][] = [...own, ...autoHeaders(d).filter(([k, v]) => !/^(user-agent|accept)$/i.test(k) && !/<made when sent>/.test(v))]
  let body: string | null = null
  let bodyEncoding: 'utf8' | 'base64' = 'utf8'
  let form: HttpFormPart[] | null = null
  if (d.body.mode === 'raw') {
    body = d.body.raw
    if (d.body.rawType === 'json' && body.trim() && !body.includes('{{')) {
      try { JSON.parse(body) } catch (err) { return { ok: false, error: `The JSON body is not valid: ${(err as Error).message}` } }
    }
  } else if (d.body.mode === 'urlencoded') {
    // a {{VARIABLE}} stays readable for the sandbox to fill in (it is encoded there as the lender gets it)
    body = d.body.urlencoded.filter((r) => r.on && r.key).map((r) => `${encodeKeepVars(r.key)}=${encodeKeepVars(r.value)}`).join('&')
  } else if (d.body.mode === 'formdata') {
    const rows = d.body.formdata.filter((r) => r.on && r.key)
    const missingFile = rows.find((r) => r.type === 'file' && !r.file)
    if (missingFile) return { ok: false, error: `Choose the file for “${missingFile.key}” again (files are not saved with a request)` }
    form = rows.map((r) => (r.type === 'file' && r.file ? { name: r.key, filename: r.file.name, contentType: r.file.type || 'application/octet-stream', base64: r.file.base64 } : { name: r.key, value: r.value }))
  } else if (d.body.mode === 'binary') {
    if (!d.body.binary?.file) return { ok: false, error: d.body.binary?.filename ? `Choose the file “${d.body.binary.filename}” again (files are not saved with a request)` : 'Choose the file to send as the body' }
    body = d.body.binary.file.base64
    bodyEncoding = 'base64'
  }
  return { ok: true, request: { method: d.method, url, headers, body: body === '' ? null : body, bodyEncoding, ...(form ? { form } : {}), timeoutMs: d.settings.timeoutMs, followRedirects: d.settings.followRedirects } }
}

// ---- paste a curl ---------------------------------------------------------------------------------------------------

/** Splits a shell line as bash would (quotes, backslashes, line ends with \ or Windows' ^). */
function shellWords(text: string): string[] {
  const s = text.replace(/\\\r?\n|\^\r?\n/g, ' ')
  const out: string[] = []
  let cur = ''
  let quote: '"' | "'" | null = null
  let any = false
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!
    if (quote === "'") { if (c === "'") quote = null; else cur += c; continue }
    if (quote === '"') {
      if (c === '"') quote = null
      else if (c === '\\' && i + 1 < s.length && '"\\$`'.includes(s[i + 1]!)) cur += s[++i]
      else cur += c
      continue
    }
    if (c === "'" || c === '"') { quote = c; any = true; continue }
    if (c === '\\' && i + 1 < s.length) { cur += s[++i]; any = true; continue }
    if (/\s/.test(c)) { if (cur || any) out.push(cur); cur = ''; any = false; continue }
    cur += c
  }
  if (cur || any) out.push(cur)
  return out
}

export const looksLikeCurl = (text: string) => /^\s*curl(\.exe)?\s/i.test(text)

/** A curl command (as lenders send them, or "Copy as cURL" from a browser) → a request. */
export function parseCurl(text: string): HttpDraft | null {
  const w = shellWords(text.trim())
  if (!/^curl(\.exe)?$/i.test(w[0] ?? '')) return null
  const d = emptyDraft()
  let method: string | null = null
  let url = ''
  const data: string[] = []
  const urlencoded: Kv[] = []
  let get = false
  for (let i = 1; i < w.length; i++) {
    const a = w[i]!
    const next = () => w[++i] ?? ''
    const [flag, inline] = a.startsWith('--') && a.includes('=') ? [a.slice(0, a.indexOf('=')), a.slice(a.indexOf('=') + 1)] : [a, null]
    const val = () => (inline !== null ? inline : next())
    switch (flag) {
      case '-X': case '--request': method = val().toUpperCase(); break
      case '-H': case '--header': {
        const h = val()
        const c = h.indexOf(':')
        if (c > 0) d.headers.push({ key: h.slice(0, c).trim(), value: h.slice(c + 1).trim(), on: true })
        break
      }
      case '-d': case '--data': case '--data-raw': case '--data-binary': case '--data-ascii': data.push(val()); break
      case '--data-urlencode': {
        const v = val()
        const e = v.indexOf('=')
        urlencoded.push({ key: e >= 0 ? v.slice(0, e) : v, value: e >= 0 ? v.slice(e + 1) : '', on: true })
        break
      }
      case '-u': case '--user': {
        const v = val()
        const c = v.indexOf(':')
        d.auth = { ...d.auth, type: 'basic', username: c >= 0 ? v.slice(0, c) : v, password: c >= 0 ? v.slice(c + 1) : '' }
        break
      }
      case '-b': case '--cookie': d.headers.push({ key: 'Cookie', value: val(), on: true }); break
      case '-A': case '--user-agent': d.headers.push({ key: 'User-Agent', value: val(), on: true }); break
      case '-e': case '--referer': d.headers.push({ key: 'Referer', value: val(), on: true }); break
      case '--url': url = val(); break
      case '-G': case '--get': get = true; break
      case '-I': case '--head': method = 'HEAD'; break
      case '-L': case '--location': d.settings.followRedirects = true; break
      case '-m': case '--max-time': d.settings.timeoutMs = Math.min(Math.max(Math.round(Number(val()) * 1000) || 30000, 1000), 60000); break
      case '-o': case '--output': case '--connect-timeout': case '-w': case '--write-out': val(); break
      default:
        if (!a.startsWith('-') && !url) url = a
    }
  }
  // a Bearer header becomes the Authorization tab, as Postman does
  const auth = d.headers.findIndex((h) => h.key.toLowerCase() === 'authorization' && /^bearer\s+/i.test(h.value))
  if (auth >= 0 && d.auth.type === 'none') { d.auth = { ...d.auth, type: 'bearer', token: d.headers[auth]!.value.replace(/^bearer\s+/i, '') }; d.headers.splice(auth, 1) }
  const type = d.headers.find((h) => h.key.toLowerCase() === 'content-type')?.value ?? ''
  if (get && data.length) url = urlWithParams(url, [...paramsFromUrl(url), ...data.join('&').split('&').map((p) => ({ key: p.split('=')[0]!, value: p.split('=').slice(1).join('='), on: true }))])
  else if (urlencoded.length || (data.length && /x-www-form-urlencoded/i.test(type))) {
    const rows = [...data.join('&').split('&').filter(Boolean).map((p) => ({ key: decodeSafe(p.split('=')[0]!), value: decodeSafe(p.split('=').slice(1).join('=')), on: true })), ...urlencoded]
    d.body = { ...d.body, mode: 'urlencoded', urlencoded: rows }
  } else if (data.length) {
    const raw = data.join('&')
    const json = /json/i.test(type) || /^\s*[[{]/.test(raw)
    d.body = { ...d.body, mode: 'raw', raw: json ? pretty(raw) : raw, rawType: json ? 'json' : /xml/i.test(type) ? 'xml' : 'text' }
  }
  // no -X: POST when there is data (curl's own rule), GET otherwise; -G sends the data in the query, so GET
  const sendsData = (data.length > 0 || urlencoded.length > 0) && !get
  const picked = method ?? (sendsData ? 'POST' : 'GET')
  d.method = (METHODS as readonly string[]).includes(picked) ? (picked as Method) : 'GET'
  d.url = url
  d.params = paramsFromUrl(url)
  return d
}

const pretty = (raw: string) => { try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw } }

// ---- the response -------------------------------------------------------------------------------------------------

export interface Cookie { name: string; value: string; attributes: string }
export const cookiesOf = (a: HttpAnswer): Cookie[] =>
  (a.headers ?? []).filter(([k]) => k.toLowerCase() === 'set-cookie').map(([, v]) => {
    const [pair = '', ...rest] = v.split(';')
    const e = pair.indexOf('=')
    return { name: pair.slice(0, e >= 0 ? e : undefined).trim(), value: e >= 0 ? pair.slice(e + 1).trim() : '', attributes: rest.map((x) => x.trim()).join('; ') }
  })

export const headerOf = (a: HttpAnswer, name: string) => (a.headers ?? []).find(([k]) => k.toLowerCase() === name.toLowerCase())?.[1] ?? ''

/** The body as the Pretty view shows it: JSON indented, anything else as it came. */
export function prettyBody(a: HttpAnswer): { text: string; json: boolean } {
  if (a.bodyEncoding === 'base64') return { text: `(binary, ${formatSize(a.size ?? 0)}; shown as base64)\n${a.body ?? ''}`, json: false }
  const raw = a.body ?? ''
  if (/json/i.test(headerOf(a, 'content-type')) || /^\s*[[{]/.test(raw)) {
    try { return { text: JSON.stringify(JSON.parse(raw), null, 2), json: true } } catch { /* not JSON after all */ }
  }
  return { text: raw, json: false }
}

export const formatSize = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(2)} MB`)
export const statusTone = (s?: number) => (!s ? 'danger' : s < 300 ? 'success' : s < 400 ? 'info' : s < 500 ? 'warning' : 'danger') as 'success' | 'info' | 'warning' | 'danger'

/** One line of the session's history: what was sent and what came back (the draft to open it again). */
export interface HistoryItem { at: string; draft: HttpDraft; status: number | null; ms: number; error: string | null }

/** x-www-form-urlencoded as browsers write it (space = +), a {{VARIABLE}} left as it is for the sandbox */
const formEncode = (s: string) => new URLSearchParams([['', s]]).toString().slice(1)
function encodeKeepVars(s: string) {
  return s.split(/(\{\{[^}]*\}\})/).map((part) => (/^\{\{[^}]*\}\}$/.test(part) ? part : formEncode(part))).join('')
}

/** A file's content through FileReader (every browser, and the tests' DOM, which has no File.text / arrayBuffer). */
const reader = <T>(f: Blob, how: 'text' | 'buffer') => new Promise<T>((resolve, reject) => {
  const r = new FileReader()
  r.onload = () => resolve(r.result as T)
  r.onerror = () => reject(r.error ?? new Error('The file could not be read'))
  if (how === 'text') r.readAsText(f)
  else r.readAsArrayBuffer(f)
})
export const fileText = (f: Blob) => reader<string>(f, 'text')

/** A file picked in the browser, as hub-http takes it (5 MB at most: the request goes through the Hub). */
export async function readFile(f: File): Promise<PickedFile> {
  if (f.size > 5 * 1024 * 1024) throw new Error(`${f.name} is ${formatSize(f.size)}: 5 MB at most`)
  const buf = new Uint8Array(await reader<ArrayBuffer>(f, 'buffer'))
  let bin = ''
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000))
  return { name: f.name, type: f.type || 'application/octet-stream', size: f.size, base64: btoa(bin) }
}

// ---- import a Postman collection (v2.0 / v2.1, as lenders send them) -----------------------------------------------

type PmKv = { key?: string; value?: unknown; disabled?: boolean; type?: string; src?: unknown }
type PmAuth = { type?: string; bearer?: PmKv[]; basic?: PmKv[]; apikey?: PmKv[] }
type PmItem = { name?: string; item?: PmItem[]; request?: string | { method?: string; url?: string | { raw?: string }; header?: PmKv[]; body?: { mode?: string; raw?: string; urlencoded?: PmKv[]; formdata?: PmKv[]; file?: { src?: string }; options?: { raw?: { language?: string } } }; auth?: PmAuth }; auth?: PmAuth }

const kvOf = (list: PmKv[] | undefined): Kv[] => (list ?? []).filter((x) => x && x.key !== undefined).map((x) => ({ key: String(x.key), value: x.value == null ? '' : String(x.value), on: !x.disabled }))
const authValue = (list: PmKv[] | undefined, key: string) => String((list ?? []).find((x) => x.key === key)?.value ?? '')
function authOf(a: PmAuth | undefined): HttpDraft['auth'] | null {
  if (!a || !a.type) return null
  const blank = emptyDraft().auth
  if (a.type === 'noauth') return blank
  if (a.type === 'bearer') return { ...blank, type: 'bearer', token: authValue(a.bearer, 'token') }
  if (a.type === 'basic') return { ...blank, type: 'basic', username: authValue(a.basic, 'username'), password: authValue(a.basic, 'password') }
  if (a.type === 'apikey') return { ...blank, type: 'apikey', key: authValue(a.apikey, 'key') || 'x-api-key', value: authValue(a.apikey, 'value'), in: authValue(a.apikey, 'in') === 'query' ? 'query' : 'header' }
  return null
}

export interface PostmanImport { name: string; items: { folder: string; name: string; request: HttpSpec }[]; variables: string[]; skipped: string[] }

/** A Postman collection file → requests in folders (auth inherited from the folder or the collection, as in Postman). */
export function importPostman(text: string): PostmanImport {
  let json: { info?: { name?: string; schema?: string }; item?: PmItem[]; auth?: PmAuth; variable?: PmKv[] }
  try { json = JSON.parse(text) } catch { throw new Error('This file is not JSON: export the collection from Postman as “Collection v2.1”') }
  if (!json || !Array.isArray(json.item)) throw new Error('This is not a Postman collection (no “item” list): export it as “Collection v2.1”')
  const items: PostmanImport['items'] = []
  const skipped: string[] = []
  const walk = (list: PmItem[], folder: string[], inherited: HttpDraft['auth'] | null) => {
    for (const it of list) {
      const name = String(it.name ?? 'Untitled').slice(0, 120)
      if (Array.isArray(it.item)) { walk(it.item, [...folder, name.slice(0, 60)], authOf(it.auth) ?? inherited); continue }
      const r = typeof it.request === 'string' ? { url: it.request } : it.request
      if (!r) { skipped.push(name); continue }
      const method = String(r.method ?? 'GET').toUpperCase()
      const url = typeof r.url === 'string' ? r.url : String(r.url?.raw ?? '')
      const d = emptyDraft({ name, method: (METHODS as readonly string[]).includes(method) ? (method as Method) : 'GET', url, params: paramsFromUrl(url), headers: kvOf(r.header), auth: authOf(r.auth) ?? inherited ?? emptyDraft().auth })
      const b = r.body
      if (b?.mode === 'raw' && b.raw) {
        const lang = b.options?.raw?.language
        d.body = { ...d.body, mode: 'raw', raw: b.raw, rawType: lang === 'xml' ? 'xml' : lang === 'text' ? 'text' : 'json' }
      } else if (b?.mode === 'urlencoded') d.body = { ...d.body, mode: 'urlencoded', urlencoded: kvOf(b.urlencoded) }
      else if (b?.mode === 'formdata') d.body = { ...d.body, mode: 'formdata', formdata: (b.formdata ?? []).map((x) => ({ key: String(x.key ?? ''), value: x.type === 'file' ? '' : String(x.value ?? ''), on: !x.disabled, type: x.type === 'file' ? 'file' : 'text', ...(x.type === 'file' ? { filename: String(Array.isArray(x.src) ? x.src[0] ?? '' : x.src ?? '').split(/[\\/]/).pop() } : {}) })) }
      else if (b?.mode === 'file') d.body = { ...d.body, mode: 'binary', binary: { filename: String(b.file?.src ?? '').split(/[\\/]/).pop() ?? '' } }
      items.push({ folder: folder.join('/').slice(0, 200), name, request: toSpec(d) })
    }
  }
  walk(json.item, [], authOf(json.auth))
  const variables = (json.variable ?? []).map((v) => String(v.key ?? '')).filter(Boolean)
  return { name: String(json.info?.name ?? 'Postman collection'), items: items.slice(0, 500), variables, skipped: [...skipped, ...items.slice(500).map((i) => i.name)] }
}
